import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Table, Button, Label, Input } from 'reactstrap'
import Select from 'react-select'
import { API_URL } from './utils/Constants/URLS'
import { useSelector } from 'react-redux'

const PackList = ({ diId, diData = null, onBack = null }) => {
  console.log(diData)
  const token = localStorage.getItem('token')
  const userDetails = useSelector(store => store.user) // currently unused, but kept if needed later

  const [stores, setStores] = useState([])
  const [loadingStores, setLoadingStores] = useState(false)

  const [loadingDI, setLoadingDI] = useState(false)
  const [di, setDi] = useState(diData || null)
  const [lineItems, setLineItems] = useState([]) // normalized items with pricing
  const [includedLines, setIncludedLines] = useState({})
  const [selectedQty, setSelectedQty] = useState({})

  const [boxNumber, setBoxNumber] = useState('BOX-001')
  const [selectedStore, setSelectedStore] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState(null) // {type:'success'|'error'|'info', text}

  // helpers
  const getLineId = (line, idx) => line?._id ?? line?.id ?? `line-${idx}`
  const getModelName = line =>
    line?.model?.modelName ??
    line?.modelName ??
    line?.name ??
    (line?.model?._id ? line.model._id : line?.model) ??
    '-'
  const getPartCode = line =>
    line?.model?.partCode ?? line?.partCode ?? line?.part_code ?? '-'

  // fetch stores
  // modelIds of the selected (included) line items only
  const selectedModelIdsKey = lineItems
    .filter(li => includedLines[li.lineId])
    .map(li => li.model)
    .filter(Boolean)
    .map(String)
    .filter((id, index, self) => self.indexOf(id) === index) // remove duplicates
    .join(',')

  // fetch stores - UPDATED to use new API endpoint
  useEffect(() => {
    const getStoreData = async () => {
      const modelIds = selectedModelIdsKey ? selectedModelIdsKey.split(',') : []

      // Only fetch if we have modelIds
      if (modelIds.length === 0) {
        setStores([])
        return
      }

      try {
        setLoadingStores(true)
        const res = await fetch(`${API_URL}stores/picklist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ modelIds })
        })
        const json = await res.json()
        setStores(json?.data?.docs || json?.data || [])
      } catch (err) {
        console.error('Error fetching stores:', err)
        setStores([])
      } finally {
        setLoadingStores(false)
      }
    }
    if (token) getStoreData()
  }, [token, selectedModelIdsKey])

  // fetch DI if not passed in
  useEffect(() => {
    const fetchDI = async () => {
      if (!diId) return
      if (diData) {
        initFromDI(diData)
        return
      }
      try {
        setLoadingDI(true)
        const res = await fetch(`${API_URL}delivery-instructions/${diId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
        console.log(res)

        if (!res.ok) {
          const text = await res.text()
          throw new Error(text || `Status ${res.status}`)
        }
        const json = await res.json()
        const payload = json?.data ?? json
        initFromDI(payload)
      } catch (err) {
        console.error('Failed to fetch DI:', err)
        setLineItems([])
      } finally {
        setLoadingDI(false)
      }
    }
    fetchDI()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diId, diData, token])

  const handleNarrationChange = (lineId, value) => {
    setLineItems(prev =>
      prev.map(li => (li.lineId === lineId ? { ...li, narration: value } : li))
    )
  }

  // initialize normalized items and defaults, now with pricing & quotePrice
  const initFromDI = diObj => {
    if (!diObj) {
      setLineItems([])
      setIncludedLines({})
      setSelectedQty({})
      return
    }

    setDi(diObj)

    // ✅ BUILD PRICING MAP FROM SALES ORDER (pricing stays constant)
    const pricingMap = new Map()
    if (Array.isArray(diObj?.salesOrder?.lineItems)) {
      diObj.salesOrder.lineItems.forEach(li => {
        const key =
          li?.model?._id ?? (typeof li.model === 'string' ? li.model : null)
        if (key) pricingMap.set(String(key), li)
      })
    }

    const items = Array.isArray(diObj.lineItems) ? diObj.lineItems : []
    const normalized = items.map((li, idx) => {
      const lineId = getLineId(li, idx)
      const modelId =
        li?.model?._id ?? (typeof li.model === 'string' ? li.model : null)

      // ✅ GET PRICING FROM SALES ORDER (constant source)
      const pricing = modelId ? pricingMap.get(String(modelId)) : null

      return {
        lineId,
        _id: li._id ?? null,
        model: li.model?._id ?? li.model,

        modelName: getModelName(li),
        partCode: getPartCode(li),
        uom: li?.model?.uom ?? li?.uom ?? '-',

        // 🔽 QUANTITY FROM DI (changes after each packlist)
        available: Number(li.quantity ?? 0),

        // 🔽 PRICING FROM SALES ORDER (stays constant)
        listPrice: Number(pricing?.listPrice ?? li.listPrice ?? 0),
        discount: Number(pricing?.discount ?? li.discount ?? 0),
        rate: Number(pricing?.rate ?? li.rate ?? 0),

        standardLeadTime: li.standardLeadTime ?? null,
        committedDeliveryDate: li.committedDeliveryDate ?? null,

        // 🔽 PL-ONLY FIELDS
        narration: '',
        gst: '',  // must be explicitly selected by user
        quotationPrice:
          (pricing?.rate ?? li.rate) != null
            ? String(pricing?.rate ?? li.rate)
            : ''
      }
    })

    setLineItems(normalized)

    const incl = {}
    const qtys = {}
    normalized.forEach(it => {
      // default include if available > 0
      incl[it.lineId] = it.available > 0
      // default quantity to 1 (if available)
      qtys[it.lineId] = it.available > 0 ? 1 : 0
    })
    setIncludedLines(incl)
    setSelectedQty(qtys)

    // default store if DI contains it
    const diStoreId = diObj?.store?._id ?? diObj?.store ?? ''
    if (diStoreId) setSelectedStore(diStoreId)
    setBoxNumber(prev => prev || 'BOX-001')
  }

  // toggle include
  const handleToggleInclude = lineId => {
    setIncludedLines(prev => {
      const next = { ...prev, [lineId]: !prev[lineId] }
      if (next[lineId] && (selectedQty[lineId] ?? 0) <= 0) {
        const li = lineItems.find(x => x.lineId === lineId)
        setSelectedQty(s => ({ ...s, [lineId]: li ? 1 : 0 }))
      }
      return next
    })
  }

  // qty change via dropdown (keeps numeric state)
  const handleQtyChange = (lineId, value) => {
    const numeric = Number(value || 0)
    const li = lineItems.find(x => x.lineId === lineId)
    const max = Number(li?.available ?? 0)
    if (numeric > max) {
      setMessage({ type: 'error', text: `Quantity cannot exceed ${max}` })
      setSelectedQty(prev => ({ ...prev, [lineId]: max }))
      return
    }
    setMessage(null)
    setSelectedQty(prev => ({ ...prev, [lineId]: numeric }))
  }

  // Allow free typing — no clamping on every keystroke
  const handleQuotationPriceChange = (lineId, value) => {
    setLineItems(prev =>
      prev.map(li =>
        li.lineId === lineId ? { ...li, quotationPrice: value } : li
      )
    )
  }

  // On blur: show error if quotation price < sell price (rate). Do NOT auto-correct.
  const handleQuotationPriceBlur = (lineId, value) => {
    const li = lineItems.find(x => x.lineId === lineId)
    if (!li) return
    const numeric = Number(value)
    const minPrice = Number(li.rate || 0)
    if (value === '' || isNaN(numeric) || numeric < minPrice) {
      setMessage({
        type: 'error',
        text: `Quotation price for "${li.modelName}" cannot be less than the sell price (₹${minPrice}). Please correct it before submitting.`
      })
    } else {
      // Clear the error only if this was the last outstanding price issue
      setMessage(prev => {
        if (prev?.type === 'error' && prev.text.includes('Quotation price')) return null
        return prev
      })
    }
  }

  const validateBeforeSubmit = () => {
    if (!diId) {
      setMessage({ type: 'error', text: 'Missing delivery instruction id.' })
      return false
    }
    if (!selectedStore) {
      setMessage({ type: 'error', text: 'Please select a store.' })
      return false
    }
    const itemsToSend = Object.keys(includedLines)
      .filter(k => includedLines[k])
      .map(k => ({ lineId: k, qty: Number(selectedQty[k] ?? 0) }))
      .filter(i => i.qty > 0)
    if (itemsToSend.length === 0) {
      setMessage({
        type: 'error',
        text: 'Please include at least one line with quantity > 0.'
      })
      return false
    }
    if (!boxNumber || boxNumber.trim().length === 0) {
      setMessage({ type: 'error', text: 'Please provide box number.' })
      return false
    }
    // Validate GST slab is selected for every included line
    const includedLineItems = lineItems.filter(
      li => includedLines[li.lineId] && (selectedQty[li.lineId] ?? 0) > 0
    )
    const missingGst = includedLineItems.some(
      li => li.gst === '' || li.gst === null || li.gst === undefined
    )
    if (missingGst) {
      setMessage({
        type: 'error',
        text: 'Please select a GST slab for all included line items.'
      })
      return false
    }
    // Validate quotation price >= sell price (rate) for all included lines
    const invalidQuote = includedLineItems.find(li => {
      const qp = Number(li.quotationPrice)
      const rp = Number(li.rate || 0)
      return isNaN(qp) || qp < rp
    })
    if (invalidQuote) {
      setMessage({
        type: 'error',
        text: `Quotation price for "${invalidQuote.modelName}" (₹${invalidQuote.quotationPrice}) cannot be less than the sell price (₹${invalidQuote.rate}). Discount cannot be negative.`
      })
      return false
    }
    return true
  }

  const handleGstChange = (lineId, value) => {
    setLineItems(prev =>
      prev.map(li =>
        li.lineId === lineId
          ? {
            ...li,
            gst: Number(value || 0) // ensure numeric
          }
          : li
      )
    )
  }

  const calculateDiscountFromQuote = (sellPrice, quotePrice) => {
    const sp = Number(sellPrice || 0)
    const qp = Number(quotePrice || 0)

    if (qp <= 0 || sp <= 0 || qp <= sp) return 0

    return Number((((qp - sp) / qp) * 100).toFixed(2))
  }

  // create pack list — include pricing fields & quotePrice to avoid nulls on server
  const handleCreatePackList = async () => {
    setMessage(null)
    if (!validateBeforeSubmit()) return

    const payloadLineItems = lineItems
      .filter(li => includedLines[li.lineId])
      .map(li => {
        const qty = Number(selectedQty[li.lineId] ?? 0)

        // ✅ CALCULATE DISCOUNT FROM QUOTE PRICE
        const calculatedDiscount = calculateDiscountFromQuote(
          li.rate,
          li.quotationPrice
        )

        return {
          model: li.model,
          quantity: qty,

          // 🔽 FROM DI (allowed)
          listPrice: li.listPrice,
          standardLeadTime: li.standardLeadTime,
          committedDeliveryDate: li.committedDeliveryDate,

          // 🔽 PL INPUTS
          narration: li.narration || '',
          quotationPrice: Number(li.quotationPrice),

          // 🔽 SELL PRICE
          rate: Number(li.rate),

          gst: Number(li.gst),

          // 🔽 ✅ OVERRIDDEN DISCOUNT (IMPORTANT)
          discount: calculatedDiscount,

          // 🔽 DERIVED VALUE
          value: Number(li.rate) * qty
        }
      })
      .filter(li => li.quantity > 0)

    const payload = {
      deliveryInstruction: diId,
      store: selectedStore,
      boxNumber: boxNumber || `BOX-${String(Date.now()).slice(-4)}`,
      lineItems: payloadLineItems
    }

    try {
      setSubmitting(true)

      // 1️⃣ Create pack list
      const res = await fetch(`${API_URL}pick-lists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })

      const txt = await res.text()
      if (!res.ok) {
        let errMsg = txt
        try {
          errMsg = JSON.parse(txt)?.message || errMsg
        } catch { }
        throw new Error(errMsg || `Status ${res.status}`)
      }

      setMessage({ type: 'success', text: 'Pack list created successfully.' })

      // 🔽 Build model → picked quantity map
      const modelSubtract = {}
      payloadLineItems.forEach(p => {
        modelSubtract[String(p.model)] =
          (modelSubtract[String(p.model)] || 0) + Number(p.quantity)
      })

      // 2️⃣ PATCH Delivery Instruction quantities
      try {
        const updatedDILineItems = (
          Array.isArray(di?.lineItems) ? di.lineItems : []
        ).map(li => {
          const modelId =
            li?.model?._id ?? (typeof li?.model === 'string' ? li.model : null)

          const oldQty = Number(li?.quantity ?? li?.qty ?? 0)
          const sub = modelId ? modelSubtract[String(modelId)] || 0 : 0
          const newQty = Math.max(0, oldQty - sub)

          // ✅ Build the update payload
          // CRITICAL: Send only model ID to backend, it will populate the full model object
          return {
            _id: li._id,
            model: modelId, // Send only the ID, backend will populate the full object
            quantity: newQty,
            listPrice: li.listPrice,
            discount: li.discount,
            rate: li.rate,
            standardLeadTime: li.standardLeadTime,
            committedDeliveryDate: li.committedDeliveryDate
          }
        })

        const diPatchRes = await fetch(
          `${API_URL}delivery-instructions/${diId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ lineItems: updatedDILineItems })
          }
        )

        if (!diPatchRes.ok) {
          const errText = await diPatchRes.text()
          console.error('DI update failed:', errText)
          setMessage({
            type: 'error',
            text: 'Pack list created, but failed to update Delivery Instruction quantities.'
          })
        } else {
          const patched = await diPatchRes.json()
          const patchedDI = patched?.data ?? patched

          if (patchedDI?.lineItems) {
            setDi(prev => ({ ...(prev || {}), ...(patchedDI || {}) }))
          } else {
            setDi(prev => {
              const base = prev || {}
              const merged = (
                Array.isArray(base.lineItems) ? base.lineItems : []
              ).map(li => {
                const modelId =
                  li?.model?._id ??
                  (typeof li?.model === 'string' ? li.model : null)

                const sub = modelId ? modelSubtract[String(modelId)] || 0 : 0
                const oldQty = Number(li?.quantity ?? li?.qty ?? 0)

                return { ...li, quantity: Math.max(0, oldQty - sub) }
              })

              return { ...base, lineItems: merged }
            })
          }
        }
      } catch (e) {
        console.error('Error while PATCHing DI:', e)
        setMessage({
          type: 'error',
          text: 'Pack list created, but updating Delivery Instruction failed.'
        })
      }

      // 3️⃣ Update UI availability
      const modelSubtractNums = {}
      payloadLineItems.forEach(p => {
        modelSubtractNums[String(p.model)] =
          (modelSubtractNums[String(p.model)] || 0) + Number(p.quantity)
      })

      setLineItems(prev =>
        prev.map(li => {
          const sub = modelSubtractNums[String(li.model)] || 0
          return { ...li, available: Math.max(0, li.available - sub) }
        })
      )

      setSelectedQty(prev => {
        const copy = { ...prev }
        lineItems.forEach(li => {
          const sub = modelSubtractNums[String(li.model)] || 0
          const newAvail = Math.max(0, li.available - sub)
          if ((copy[li.lineId] ?? 0) > newAvail) {
            copy[li.lineId] = newAvail > 0 ? 1 : 0
          }
        })
        return copy
      })

      setIncludedLines(prev => {
        const next = { ...prev }
        lineItems.forEach(li => {
          const sub = modelSubtractNums[String(li.model)] || 0
          const newAvail = Math.max(0, li.available - sub)
          if (newAvail === 0) next[li.lineId] = false
        })
        return next
      })

      // 4️⃣ Prepare for next pack list
      setBoxNumber(`BOX-${String(Date.now()).slice(-5)}`)

      if (typeof onBack === 'function') onBack()
      window.location.reload()
    } catch (err) {
      console.error('Create pick list error:', err)
      setMessage({
        type: 'error',
        text: `Failed to create pack list: ${err.message || err}`
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Render
  return (
    <div className='w-full'>
      <div className='card shadow-sm'>
        <div className='card-body'>
          {message && (
            <div
              className={`alert ${message.type === 'success'
                ? 'alert-success'
                : message.type === 'error'
                  ? 'alert-danger'
                  : 'alert-info'
                }`}
              role='alert'
            >
              {message.text}
            </div>
          )}

          <Row className='g-3 mb-3'>
            <Col md={4}>
              <Label className='form-label fw-semibold'>Store</Label>
              <select
                className='form-select'
                value={selectedStore}
                onChange={e => setSelectedStore(e.target.value)}
                disabled={loadingStores || submitting}
              >
                <option value=''>Select store</option>
                {stores.map(s => (
                  <option key={s._id ?? s.id} value={s._id ?? s.id}>
                    {s.name ?? s.storeName ?? s._id ?? s.id}
                  </option>
                ))}
              </select>
            </Col>

            <Col md={4}>
              <Label className='form-label fw-semibold'>Box Number</Label>
              <Input
                value={boxNumber}
                onChange={e => setBoxNumber(e.target.value)}
                placeholder='BOX-001'
                disabled={submitting}
              />
            </Col>
          </Row>

          <div className='table-responsive'>
            <Table className='table table-striped table-bordered'>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Include</th>
                  <th style={{ width: 40 }}>SI</th>
                  <th>Name</th>
                  <th>Narration</th>
                  <th>Part Code</th>
                  <th>Sell Price</th>
                  <th>Quote Price</th>
                  <th>GST</th>
                  <th>UOM</th>
                  <th style={{ width: 140 }}>Available</th>
                  <th style={{ width: 160 }}>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {loadingDI ? (
                  <tr>
                    <td colSpan='9' className='text-center py-4'>
                      Loading DI...
                    </td>
                  </tr>
                ) : lineItems.length === 0 ? (
                  <tr>
                    <td colSpan='9' className='text-center py-4'>
                      No line items
                    </td>
                  </tr>
                ) : (
                  lineItems.map((li, idx) => {
                    const included = !!includedLines[li.lineId]
                    const curQty = Number(selectedQty[li.lineId] ?? 0)

                    // build qtyOptions descending from available to 1 (or 0 if none)
                    const qtyOptions =
                      li.available > 0
                        ? Array.from({ length: li.available }, (_, i) => {
                          const val = li.available - i
                          return { value: val, label: String(val) }
                        })
                        : [{ value: 0, label: '0' }]

                    // find the matching option for current qty (fallback to first option)
                    const selectedOption =
                      qtyOptions.find(o => o.value === curQty) ||
                      qtyOptions[0] ||
                      null

                    return (
                      <tr key={li.lineId}>
                        <td className='text-center'>
                          <input
                            type='checkbox'
                            checked={included}
                            onChange={() => handleToggleInclude(li.lineId)}
                            disabled={submitting}
                          />
                        </td>
                        <td>{idx + 1}</td>
                        <td>{li.modelName}</td>
                        <td>
                          <Input
                            type='text'
                            placeholder='Enter narration'
                            value={li.narration}
                            onChange={e =>
                              handleNarrationChange(li.lineId, e.target.value)
                            }
                            disabled={submitting}
                          />
                        </td>
                        <td>{li.partCode}</td>
                        <td>{li.rate}</td>
                        <td>
                          <input
                            className='form-control'
                            type='number'
                            min={li.rate}
                            value={li.quotationPrice ?? ''}
                            onChange={e =>
                              handleQuotationPriceChange(
                                li.lineId,
                                e.target.value
                              )
                            }
                            onBlur={e =>
                              handleQuotationPriceBlur(
                                li.lineId,
                                e.target.value
                              )
                            }
                            onWheel={e => e.target.blur()}
                            disabled={submitting}
                          />
                        </td>
                        <td>
                          <select
                            className={`form-select${included && li.gst === '' ? ' is-invalid' : ''}`}
                            value={li.gst}
                            onChange={e =>
                              handleGstChange(li.lineId, e.target.value)
                            }
                            disabled={submitting}
                          >
                            <option value=''>Select</option>
                            <option value={0.1}>0.1</option>
                            <option value={5}>5</option>
                            <option value={12}>12</option>
                            <option value={18}>18</option>
                            <option value={28}>28</option>
                          </select>
                        </td>

                        <td>{li.uom}</td>
                        <td>{li.available}</td>
                        <td>
                          <div style={{ maxWidth: 140 }}>
                            <Select
                              options={qtyOptions}
                              value={selectedOption}
                              onChange={opt =>
                                handleQtyChange(li.lineId, opt?.value ?? 0)
                              }
                              isSearchable
                              isDisabled={
                                !included || submitting || li.available <= 0
                              }
                              menuPlacement='auto'
                              aria-label={`Quantity for ${li.modelName}`}
                              styles={{
                                container: base => ({
                                  ...base,
                                  width: '100%'
                                }),
                                menu: base => ({ ...base, zIndex: 9999 })
                              }}
                              placeholder={
                                li.available > 0 ? 'Select qty' : '0'
                              }
                            />
                          </div>
                          <small className='text-muted'>
                            Max: {li.available}
                          </small>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </Table>
          </div>

          <div className='d-flex justify-content-end gap-2 mt-3'>
            <Button
              color='primary'
              onClick={handleCreatePackList}
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Pack List'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

PackList.propTypes = {
  diId: PropTypes.string.isRequired,
  diData: PropTypes.object,
  onBack: PropTypes.func
}

export default PackList
